import { QueryCache, QueryClient, MutationCache, type MutationMeta, type QueryMeta } from "@tanstack/react-query";
import { addError, addWarning, is4XXAxiosError } from "@wayflyer/blackbox-fe";
import { isPermissionDeniedError, isSessionExpiredError } from "@wayflyer/auth-sdk";
import { isAxiosError, isCancel } from "axios";

import { getEnvironment } from "./environment";

export const reactQueryCacheConfig = {
  onError: (error: unknown, { meta }) => {
    // Ahead of `meta.onError`, which several queries use to report failures themselves: an
    // aborted request is not one, whoever is doing the reporting.
    if (isCancel(error)) {
      return;
    }
    if (typeof meta?.onError === "function") {
      meta.onError(error);
    }
    handleError(error, meta);
  },
};

const reactMutationCacheConfig = {
  onError: (error: unknown, _variables, _context, { meta }) => {
    handleError(error, meta);
  },
};

function handleError(error: unknown, meta?: MutationMeta | QueryMeta) {
  // Session expiry is routine, and worth naming: the raw axios message is "Request failed with status code 403",
  // which says nothing about why. The error itself is still attached, so status, URL and body remain available.
  //
  // Every query in flight fails together when a session lapses, and each one rejects with its own error. The
  // dedupe key reports the lapse once rather than once per query. The user reaches the login page either way,
  // so the number of queries in flight is not information anyone acts on.
  //
  // This sits above `meta.logError === false`. A lapsed session is not the query's own error, and a query that
  // silences its errors still throws to the session-expired page. Reporting here also marks the error, which
  // keeps an error boundary from reporting the same rejection as a generic 403.
  if (isSessionExpiredError(error)) {
    addWarning(typeof meta?.logError === "string" ? meta.logError : "Session expired", {}, error, {
      dedupeKey: "session-expired",
    });
    return;
  }

  // An autocomplete aborts its in-flight request on every superseded keystroke, which axios
  // reports as a cancellation. The caller catching it does not help: the query still enters its
  // error state, so this handler runs regardless of who awaits the promise.
  if (isCancel(error)) {
    return;
  }

  if (meta?.logError === false) {
    return;
  }

  const axiosError = isAxiosError(error);

  // axios error and 4xx
  if (axiosError && is4XXAxiosError(error)) {
    if (typeof meta?.logError === "string") {
      addWarning(meta.logError, {}, error);
    } else {
      addWarning(error.message, {}, error);
    }
    return;
  }

  // all other kinds of errors
  if (typeof meta?.logError === "string") {
    addError(new Error(meta.logError, { cause: error }));
  } else {
    addError(error);
  }
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        if (["test", "storybook"].includes(getEnvironment())) {
          return false;
        }
        // Don't retry on 400/404 — the request is wrong, retrying won't help.
        // Other 4xx (408, 429, etc.) may be worth retrying.
        const status = isAxiosError(error) ? error.response?.status : undefined;
        if (status === 400 || status === 404 || isPermissionDeniedError(error)) {
          return false;
        }
        // Retrying an expired session just delays the login prompt by three round trips.
        if (isSessionExpiredError(error)) {
          return false;
        }
        return failureCount < 3;
      },
      throwOnError: isSessionExpiredError,
    },
    mutations: {
      throwOnError: isSessionExpiredError,
    },
  },
  queryCache: new QueryCache(reactQueryCacheConfig),
  mutationCache: new MutationCache(reactMutationCacheConfig),
});
